<script setup lang="tsx">
import { categories, securityNeed } from '@/constants/method'
import { High } from '@/models/security-method-enums-generated'
import { newMethodForm, newRuleForm } from '@/services/utils'
import type { MethodForm, RuleForm } from '@/types/module'
import { useI18n } from 'vue-i18n'
import IconButton from '../shared/IconButton.vue'
import { validCategories } from '@/services/validate'

const props = defineProps<{
	onDeleteMethod?: (id?: number) => void
	onDeleteRule?: (id?: number) => void
}>()

const model = defineModel<MethodForm[]>({ required: true })

const { t } = useI18n()

const deleteMethod = (methods: MethodForm[], index: number) => {
	const { onDeleteMethod } = props

	const [deleted] = methods.splice(index, 1)

	onDeleteMethod?.(deleted.id)
}

const deleteRule = (rules: RuleForm[], index: number) => {
	const { onDeleteRule } = props

	const [deleted] = rules.splice(index, 1)

	onDeleteRule?.(deleted.id)
}
</script>

<template>
	<div class="card mb-3" v-for="(method, i) in model" :key="i" data-test="method-card" :data-test-id="method.id">
		<div class="card-header d-flex justify-content-between align-items-center">
			<h5 class="mb-0">{{ `${t('measure')} ${i + 1}` }}</h5>
			<IconButton
				v-if="model.length > 1"
				id="delete-method"
				data-test="delete-method"
				class="bi bi-trash text-danger"
				@click="deleteMethod(model, i)"
				:title="$t('misc.delete')"
				size="fs-5"
			/>
		</div>

		<div class="card-body">
			<div class="form-group row mb-2">
				<label for="description" class="col-5 col-form-label">{{ t('description') }}</label>
				<div class="col-7">
					<textarea
						class="form-control"
						id="method-description"
						data-test="method-description"
						v-model="method.description"
						maxlength="200"
						required
					/>
				</div>
			</div>

			<div class="form-group row mb-2">
				<label for="type" class="col-5 col-form-label">{{ t('method.securityType') }}</label>
				<div class="col-7">
					<select v-model="method.type" id="method-type" data-test="method-type" class="form-select" required>
						<option v-for="option in securityNeed" :key="option" :value="option">
							{{ t(option) }}
						</option>
					</select>
				</div>
			</div>

			<div class="form-group row mt-4 mb-2" v-if="method.type === High">
				<label for="categories" class="col-5 col-form-label">{{ t('method.category.label', 2) }}</label>

				<div class="col-7">
					<div class="mb-2">{{ t('method.category.error.selectAtLeastOne') }}:</div>
					<div id="categories" v-for="field in categories" :key="field" class="form-check">
						<input
							class="form-check-input"
							type="checkbox"
							:id="`method-${field}`"
							:data-test="`method-${field}`"
							v-model="method[field]"
							:true-value="true"
							:false-value="false"
							:required="!validCategories(method)"
						/>
						<label class="form-check-label" :for="field">
							{{ t(field) }}
						</label>
					</div>
				</div>
			</div>

			<div class="form-group row mb-2">
				<label for="module-form-force" class="col-5 col-form-label"> {{ t('method.force') }}</label>
				<div class="col-7">
					<div class="form-check form-switch align-self-center ms-auto status-force-switch">
						<input
							v-model="method.status.force"
							class="form-check-input"
							type="checkbox"
							role="switch"
							id="module-form-force"
							data-test="module-form-force"
						/>
					</div>
				</div>
			</div>

			<div class="form-group row mb-2">
				<label for="rules" class="col-5 col-form-label">{{ t('rule.label', 2) }}</label>
				<div class="px-2 mt-2">
					<div class="card mb-2" v-for="(rule, i) in method.rules" :key="i" data-test="rule-card" :data-test-id="rule.id">
						<div class="card-body">
							<div class="d-flex justify-content-between align-items-center mb-2">
								<h5 class="card-title">{{ `${t('rule.label')} ${i + 1}` }}</h5>
								<IconButton
									v-if="method.rules.length > 1"
									id="'delete-rule"
									data-test="delete-rule"
									class="bi bi-trash text-danger"
									@click="deleteRule(method.rules, i)"
									:title="$t('misc.delete')"
									size="fs-5"
								/>
							</div>
							<div class="form-group mb-2">
								<label for="description" class="col-5 col-form-label">{{ t('description') }}</label>
								<div>
									<textarea class="form-control" id="rule-value" data-test="rule-value" maxlength="500" v-model="rule.value" required />
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="d-flex justify-content-center">
				<button type="button" class="btn btn-outline-secondary" data-test="add-rule" @click="method.rules.push(newRuleForm())">
					<i class="bi bi-plus-lg"></i> {{ t('rule.addRule') }}
				</button>
			</div>
		</div>
	</div>
	<div class="d-flex justify-content-center">
		<button type="button" class="btn btn-outline-secondary" data-test="add-method" @click="model.push(newMethodForm())">
			<i class="bi bi-plus-lg"></i> {{ t('method.addMeasure') }}
		</button>
	</div>
</template>
